import React from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Trash2, GripVertical, Plus } from 'lucide-react';

const DraggablePrizes = ({ prizes = [], onChange, language = 'es' }) => {
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(prizes);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    onChange(items);
  };

  const addPrize = () => {
    onChange([...prizes, { place: '', amount: '', description: '' }]);
  };

  const updatePrize = (index, field, value) => {
    const updated = [...prizes];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removePrize = (index) => {
    onChange(prizes.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4 pt-4 border-t border-gray-300">
      <div className="flex justify-between items-center">
        <div>
          <Label className="text-lg font-semibold">{language === 'es' ? 'Premios' : 'Prizes'}</Label>
          <p className="text-xs text-gray-500">
            {language === 'es' ? 'Arrastra para reordenar. El monto se mostrara con $ automaticamente.' : 'Drag to reorder. Amount will display with $ automatically.'}
          </p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={addPrize} className="border-2">
          <Plus className="w-4 h-4 mr-1" /> {language === 'es' ? 'Agregar Premio' : 'Add Prize'}
        </Button>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="prizes-list">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
              {prizes.map((prize, index) => (
                <Draggable key={`prize-${index}`} draggableId={`prize-${index}`} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`p-4 border-2 rounded-lg space-y-2 relative transition-shadow ${snapshot.isDragging ? 'border-purple-400 shadow-lg bg-purple-50' : 'border-gray-200 bg-white'}`}
                    >
                      <div className="flex items-center gap-2">
                        <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
                          <GripVertical className="w-5 h-5" />
                        </div>
                        <div className="flex-1 grid grid-cols-2 gap-3 pr-8">
                          <div className="space-y-1">
                            <Label className="text-sm">{language === 'es' ? 'Lugar / Categoria' : 'Place / Category'}</Label>
                            <Input
                              value={prize.place}
                              onChange={(e) => updatePrize(index, 'place', e.target.value)}
                              placeholder={language === 'es' ? `Ej: ${index + 1}${index === 0 ? 'er' : 'do'} lugar` : `e.g. ${index + 1}${index === 0 ? 'st' : index === 1 ? 'nd' : 'rd'} place`}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-sm">{language === 'es' ? 'Monto' : 'Amount'}</Label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">$</span>
                              <Input
                                value={prize.amount}
                                onChange={(e) => updatePrize(index, 'amount', e.target.value)}
                                placeholder="50.000"
                                className="pl-7"
                              />
                            </div>
                          </div>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removePrize(index)}
                          className="absolute top-2 right-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="space-y-1 ml-7">
                        <Label className="text-sm">{language === 'es' ? 'Descripcion (opcional)' : 'Description (optional)'}</Label>
                        <Input
                          value={prize.description || ''}
                          onChange={(e) => updatePrize(index, 'description', e.target.value)}
                          placeholder={language === 'es' ? 'Ej: Mentoria + capital semilla' : 'e.g. Mentorship + seed capital'}
                        />
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {prizes.length === 0 && (
        <p className="text-sm text-gray-400 italic text-center py-2">
          {language === 'es' ? 'Sin premios agregados. Haz clic en "Agregar Premio" para comenzar.' : 'No prizes added. Click "Add Prize" to start.'}
        </p>
      )}
    </div>
  );
};

export default DraggablePrizes;
